/**
 * The profile screen's own formatters.
 *
 * Pure, and here rather than in `garage/format.ts` because none of it is about a
 * tank: a date, a signed ledger amount and a rendered legal document. Money is
 * `formatPrice`'s job and is imported rather than restated.
 *
 * All three are the sort of thing a screenshot cannot check — a date rendered in
 * the wrong month, a spend shown as a credit, a document rendered as markup — so
 * they are functions with tests rather than expressions in a template.
 */

/**
 * An ISO instant as a plain calendar date.
 *
 * Fixed to `en-GB` for the reason `formatPrice` is: the interface is English, so
 * a machine set to US locale rendering `8/21/2026` beside English copy is the
 * interface disagreeing with itself. This is a *date*, not a currency — nothing
 * about it is the player's choice.
 *
 * An unparseable value comes back as an em dash rather than `Invalid Date`, which
 * is what a browser prints and what an empty cell should say.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** The same instant with the time, for a purchase row where order matters. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return `${formatDate(iso)}, ${at.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * A signed ledger amount, with its sign shown explicitly.
 *
 * The server sends purchases negative for a spend and positive for a grant, and
 * the pane reads as a statement — so a grant needs a `+` rather than merely the
 * absence of a `-`. Without it a row saying `100` beside one saying `-2` looks
 * like inconsistent formatting rather than opposite directions.
 */
export function formatSigned(amount: number): string {
  const magnitude = Math.abs(Math.round(amount)).toLocaleString('en-GB');
  return amount < 0 ? `−${magnitude}` : `+${magnitude}`;
}

/**
 * How a legal document's status reads when the CMS has no page for it.
 *
 * An operator who has not written a privacy policy yet is a real state, and the
 * screen says so plainly. The alternative — an error, or an empty panel — reads as
 * the app being broken rather than as the document being absent.
 */
export function legalStatus(document: { content: string } | null): 'ready' | 'unpublished' {
  return document && document.content.trim().length > 0 ? 'ready' : 'unpublished';
}

/**
 * Which of the three invoice states a row is in, worded for a player.
 *
 * `PENDING` is the state every invoice is in while `UnpaidPaymentProvider` is
 * bound — it takes no money, so nothing is ever marked paid, which is the honest
 * statement about a free grant. Calling that "Pending" to a player would imply
 * they owe something, so it reads as *Not charged* instead. See
 * `WalletService.billFor`.
 */
export function invoiceStatusLabel(status: string): string {
  switch (status.toUpperCase()) {
    case 'PAID':
      return 'Paid';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return 'Not charged';
  }
}
